import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

interface TurnstileProps {
  sitekey: string;
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  size?: "normal" | "flexible" | "compact";
  appearance?: "always" | "execute" | "interaction-only";
  /** "render" = verify on page load (default); "execute" = verify only when execute() is called */
  execution?: "render" | "execute";
}

export interface TurnstileRef {
  reset: () => void;
  execute: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact";
          appearance?: "always" | "execute" | "interaction-only";
          execution?: "render" | "execute";
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
      execute: (widgetId: string) => void;
    };
  }
}

export const Turnstile = forwardRef<TurnstileRef, TurnstileProps>(
  ({ sitekey, onSuccess, onError, onExpire, size, appearance, execution }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);

    const activeSitekey = sitekey || "0x4AAAAAADd_y307C4Y_C8fG";
    const sitekeyRef = useRef(activeSitekey);
    sitekeyRef.current = activeSitekey;

    // Store callbacks in refs so the widget useEffect never needs to re-run
    // when the parent re-renders with new inline arrow function references.
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    const onExpireRef = useRef(onExpire);
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (e) {
            console.error("[Turnstile] reset error:", e);
          }
        }
      },
      execute: () => {
        if (!window.turnstile) {
          console.warn("[Turnstile] execute() called but window.turnstile is not loaded yet.");
          return;
        }
        // If widget failed to initialize (e.g. after a reset), re-render it first
        if (!widgetIdRef.current && containerRef.current) {
          try {
            const id = window.turnstile.render(containerRef.current, {
              sitekey: sitekeyRef.current,
              callback: (token: string) => { onSuccessRef.current(token); },
              "error-callback": () => { if (onErrorRef.current) onErrorRef.current(); },
              "expired-callback": () => { if (onExpireRef.current) onExpireRef.current(); },
            });
            widgetIdRef.current = id;
          } catch (e) {
            console.error("[Turnstile] re-render on execute error:", e);
          }
        }
        if (widgetIdRef.current) {
          try {
            window.turnstile.execute(widgetIdRef.current);
          } catch (e) {
            console.error("[Turnstile] execute error:", e);
          }
        }
      },
    }));

    // This effect only runs once (on mount) and when sitekey/render options change.
    // Callbacks are intentionally excluded from the dep array — they are read
    // from refs so they are always up-to-date without causing re-renders.
    useEffect(() => {
      let script = document.getElementById("cloudflare-turnstile-script") as HTMLScriptElement;

      const initializeTurnstile = () => {
        if (window.turnstile && containerRef.current && !widgetIdRef.current) {
          try {
            const id = window.turnstile.render(containerRef.current, {
              sitekey: sitekeyRef.current,
              callback: (token: string) => { onSuccessRef.current(token); },
              "error-callback": () => { if (onErrorRef.current) onErrorRef.current(); },
              "expired-callback": () => { if (onExpireRef.current) onExpireRef.current(); },
              size: size || "normal",
              appearance: appearance || "always",
              execution: execution || "render",
            });
            widgetIdRef.current = id;
          } catch (e) {
            console.error("[Turnstile] render error:", e);
          }
        }
      };

      if (!script) {
        script = document.createElement("script");
        script.id = "cloudflare-turnstile-script";
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          if (window.turnstile) {
            initializeTurnstile();
          } else {
            const checkInterval = setInterval(() => {
              if (window.turnstile) {
                clearInterval(checkInterval);
                initializeTurnstile();
              }
            }, 100);
            return () => clearInterval(checkInterval);
          }
        };
        document.body.appendChild(script);
      } else {
        if (window.turnstile) {
          initializeTurnstile();
        } else {
          const handleScriptLoad = () => { initializeTurnstile(); };
          script.addEventListener("load", handleScriptLoad);
          return () => { script.removeEventListener("load", handleScriptLoad); };
        }
      }

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
          } catch (e) {
            console.error("[Turnstile] remove error:", e);
          }
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSitekey, size, appearance, execution]);

    return <div ref={containerRef} data-sitekey={activeSitekey} />;
  }
);

Turnstile.displayName = "Turnstile";
export default Turnstile;
